import { Button, Fieldset, Flex, ScrollArea, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect } from 'react';

import { Customer } from '@e-origin/shared';

import { activeIcon, inactiveIcon } from '@assets';
import { LeavePageNotification, StickyContainer } from '@components';
import { CustomerViewTabs, EditCustomerType } from '@enums';
import { useSettings } from '@hooks/useSettings';
import { CustomerViewEvaluationCalculator } from './CustomerViewEvaluationCalculator';
import { CustomerViewEvaluationIMADBStatisticalValue } from './CustomerViewEvaluationIMADBStatisticalValue';
import { CustomerViewEvaluationMethodAIHsCode } from './CustomerViewEvaluationMethodAIHsCode';
import { CustomerViewEvaluationMethodInspection } from './CustomerViewEvaluationMethodInspection';
import { CustomerViewEvaluationMethodWebValue } from './CustomerViewEvaluationMethodWebValue';
import { CustomerViewEvaluationMethodsFormValues, useCustomerViewEvaluationMethodsFormInitializer } from './useCustomerViewEvaluationMethodsFormInitializer';

export const renderActiveMethodButton = (form: UseFormReturnType<CustomerViewEvaluationMethodsFormValues>, activeMethod: boolean, fieldName: string) => (
  <UnstyledButton onClick={() => form.setFieldValue(fieldName, !activeMethod)}>
    <img src={activeMethod ? activeIcon : inactiveIcon} />
  </UnstyledButton>
);

interface CustomerViewEvaluationMethodsProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  customerDetails: Customer;
  isCreate: boolean;
  isButtonDisabled: boolean;
  handleTabValidityChanged: (tab, isValid) => void;
  readOnly?: boolean;
}

export const CustomerViewEvaluationMethods: React.FC<CustomerViewEvaluationMethodsProps> = ({
  isButtonDisabled,
  handleSubmitCustomer,
  customerDetails,
  isCreate,
  handleTabValidityChanged,
  readOnly = false,
}) => {
  const { data: settings } = useSettings();
  const { form, buildFormValues } = useCustomerViewEvaluationMethodsFormInitializer({ customerDetails });

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  useEffect(() => {
    handleTabValidityChanged(CustomerViewTabs.EVALUATION_METHODS, form.isValid());
  }, [form.isValid()]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} onContinue={() => handleTabValidityChanged(CustomerViewTabs.EVALUATION_METHODS, true)} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Fieldset disabled={readOnly} variant="unstyled">
          <Flex direction="row" wrap="wrap" gap="xl" mx="xl">
            <CustomerViewEvaluationMethodAIHsCode form={form} />
            <CustomerViewEvaluationCalculator form={form} />
            <CustomerViewEvaluationMethodWebValue form={form} />
            <CustomerViewEvaluationIMADBStatisticalValue form={form} />
            {settings.isForwarder && <CustomerViewEvaluationMethodInspection form={form} />}
          </Flex>
        </Fieldset>

        {!readOnly && (
          <StickyContainer>
            <Button
              disabled={isButtonDisabled || !form.isValid()}
              onClick={() => handleSubmitCustomer(EditCustomerType.EVALUATION_METHODS, form)}
              type="submit"
              variant="filled"
              size="lg"
            >
              {isCreate ? 'Create' : 'Save'}
            </Button>
          </StickyContainer>
        )}
      </ScrollArea>
    </>
  );
};
